import React, {
  useState, useMemo, useRef, useEffect,
} from 'react'
import {
  LoadManager, PageHeader, Styles, Form,
} from '@/components'
import usePageTitle from '@/hooks/usePageTitle'
import useAuth from '@/hooks/useAuth'
import useAxiosRequest from '@/hooks/useAxiosRequest'
import partnerLicensings from '@/services/api/settings/partnerLicensings'
import yup from '@/lib/yupPt'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import session from '@/services/auth/session'
import Email from './components/Email'
import ConfirmEmail from './components/ConfirmEmail'

const schemas = {
  1: yup.object().shape({
    email: yup
      .string()
      .email('E-mail inválido')
      .required('E-mail é obrigatório'),
  }),
  2: yup.object().shape({
    token: yup
      .string()
      .length(6, 'O código deve ter 6 dígitos')
      .required('Código é obrigatório'),
  }),
}

function ChangeEmail({ title }) {
  usePageTitle({ title })
  const { refreshUser } = useAuth()
  const {
    data, loading, error,
  } = useAxiosRequest(partnerLicensings.current)
  const formRef = useRef(null)
  const [step, setStep] = useState(1)
  const [newEmail, setNewEmail] = useState(null)
  const [changingEmail, setChangingEmail] = useState(false)

  const schema = useMemo(() => schemas[step], [step])

  useEffect(() => {
    formRef.current?.validateForm()
  }, [schema])

  async function onSubmit(values) {
    if (!data?.licensed?.email) return
    try {
      setChangingEmail(true)
      await session.updateCurrentUser({
        email: values.email,
      })
      toast.success('E-mail alterado. Verifique sua caixa de entrada.')
      setNewEmail(values.email)
      await refreshUser()
      setStep(2)
    } catch (e) {
      toast.error('Erro ao alterar o e-mail')
    } finally {
      setChangingEmail(false)
    }
  }

  return (
    <Styles.Container className="grid-xl">
      <PageHeader
        title="Alterar e-mail"
        icon="fas fa-envelope"
        subtitle="Altere o e-mail de acesso ao sistema"
      />
      <LoadManager loading={loading} error={error}>
        <fieldset disabled={changingEmail}>
          <Form.Formik
            innerRef={formRef}
            initialValues={{
              email: '',
              token: '',
            }}
            validationSchema={schema}
            onSubmit={onSubmit}
            validateOnMount
          >
            {({
              values, errors, touched, isValid, setFieldValue,
            }) => (
              <Form.ValidationForm>
                <div className="panel">
                  <div className="panel-header" />
                  <div className="panel-nav">
                    <ul className="step">
                      <li className={`step-item ${step === 1 ? 'active' : ''}`}>
                        <a className="tooltip" data-tooltip="E-mail">
                          E-mail
                        </a>
                      </li>
                      <li className={`step-item ${step === 2 ? 'active' : ''}`}>
                        <a className="tooltip" data-tooltip="Validar token">
                          Validar token
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="panel-body mt-2">
                    {step === 1 && (
                      <Email
                        currentPerson={data?.licensed}
                        newEmail={newEmail}
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldValue={setFieldValue}
                      />
                    )}
                    {step === 2 && (
                      <ConfirmEmail
                        values={values}
                        errors={errors}
                        touched={touched}
                        email={newEmail || ''}
                        onChangeEmail={() => {
                          setFieldValue('token', '')
                          setStep(1)
                        }}
                      />
                    )}
                  </div>
                  <div className="panel-footer text-right">
                    {step === 1 && (
                      <button
                        type="submit"
                        className={`btn btn-primary ${
                          !isValid || changingEmail ? 'disabled' : ''
                        }`}
                      >
                        Alterar e-mail &nbsp;
                        <i className="fas fa-check" />
                      </button>
                    )}
                  </div>
                </div>
              </Form.ValidationForm>
            )}
          </Form.Formik>
        </fieldset>
      </LoadManager>
    </Styles.Container>
  )
}

ChangeEmail.propTypes = {
  title: PropTypes.string.isRequired,
}

export default ChangeEmail
